import { EWorksheetDecision, EWorksheetStatus } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { Gavel as GavelIcon } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Chip, ChipProps, Stack, Typography } from '@mui/material'
import { EasyCareWorksheet } from '@src/data/types/EasyCareWorksheetSchema'
import { useTranslation } from 'react-i18next'

interface DecisionDisabledArgs {
  status: EWorksheetStatus
  editLocked: boolean
  finalDecisionIsPending: boolean
  canEditWorksheet: boolean
}

export const isDecisionDisabled = ({
  status,
  editLocked,
  finalDecisionIsPending,
  canEditWorksheet,
}: DecisionDisabledArgs): boolean =>
  status !== EWorksheetStatus.Active || editLocked || finalDecisionIsPending || !canEditWorksheet

const isApproval = (decision: EWorksheetDecision) =>
  decision === EWorksheetDecision.Approved || decision === EWorksheetDecision.ApprovedWithCondition

const decisionColor = (decision: EWorksheetDecision): ChipProps['color'] => {
  if (isApproval(decision)) return 'success'
  if (decision === EWorksheetDecision.Declined) return 'error'
  return 'warning'
}

const Figure = ({ label, value }: { label: string; value: string }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" component="p">
      {value}
    </Typography>
  </Stack>
)

interface Props {
  worksheet: EasyCareWorksheet
  interestRate: number
  decisionDisabled: boolean
  onDecisionClick: () => void
}

const EasyCareSummaryBand = ({ worksheet, interestRate, decisionDisabled, onDecisionClick }: Props) => {
  const { t } = useTranslation()
  const decision = worksheet.finalDecision?.decision ?? EWorksheetDecision.Pending
  const extend = isApproval(decision) ? worksheet.fundingThresholdExtend : null

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          <Box
            sx={{
              flex: 1,
              width: '100%',
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
            }}
          >
            <Figure label={t('worksheet.totalAmountFinanced')} value={formatCurrency(worksheet.totalAmountFinanced)} />
            <Figure label={t('easyCare.loanTerm')} value={t('easyCare.months', { count: worksheet.term })} />
            <Figure label={t('worksheet.interestRate')} value={`${interestRate} %`} />
            <Figure label={t('easyCare.payment')} value={formatCurrency(worksheet.paymentAmountForFrequency)} />
            <Figure
              label={t('worksheet.paymentFrequency')}
              value={t(`EPaymentFrequency.${worksheet.paymentFrequency}`, { ns: 'enum' })}
            />
          </Box>
          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Chip
              variant="outlined"
              color={decisionColor(decision)}
              label={t(`EWorksheetDecision.${decision}`, { ns: 'enum' })}
            />
            {!!extend?.maxTermDuration && (
              <Typography variant="caption">{`${t('worksheet.maxTerme')}: +${extend.maxTermDuration} ${t('worksheet.month')}`}</Typography>
            )}
            {!!extend?.maxPmtAmount && (
              <Typography variant="caption">{`${t('credit.maxPayment')}: +${formatCurrency(extend.maxPmtAmount)}`}</Typography>
            )}
            {!!extend?.maxTotalAmountFinanced && (
              <Typography variant="caption">{`${t('editDecisionDialog.maxTotalAmountFinanced')}: ${formatCurrency(extend.maxTotalAmountFinanced)}`}</Typography>
            )}
            <Button variant="contained" startIcon={<GavelIcon />} onClick={onDecisionClick} disabled={decisionDisabled}>
              {t('credit.decision')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default EasyCareSummaryBand
